// PDF generation via browser print — supports Arabic/RTL natively

export function generateChecklistPDF({ classification, accord, calcul, documents, economies, description, pays, paysNom, codeActivite, date, lang }) {
  const isAr = lang === 'ar';
  const dir = isAr ? 'rtl' : 'ltr';
  const L = (fr, ar) => isAr ? ar : fr;

  const dapsRow = calcul?.DAPS ? `<tr><td>DAPS (${calcul.daps_taux}%)</td><td>${fmt(calcul.DAPS)} DZD</td></tr>` : '';
  const calculSection = calcul ? `
    <h2>${L('ESTIMATION FISCALE','التقدير الجبائي')}</h2>
    <table class="fisc">
      <tr><td>${L('Valeur CIF (DZD)','القيمة CIF (د.ج)')}</td><td>${fmt(calcul.valeur_cif_dzd)} DZD</td></tr>
      <tr><td>${L('Droits de Douane','الرسوم الجمركية')} (${calcul.taux_DD_applique}%)</td><td>${fmt(calcul.DD)} DZD</td></tr>
      <tr><td>TCS (2%)</td><td>${fmt(calcul.TCS)} DZD</td></tr>
      ${dapsRow}
      <tr><td>${L('TVA','الرسم على القيمة المضافة')}</td><td>${fmt(calcul.TVA)} DZD</td></tr>
      <tr class="total"><td>${L('TOTAL TAXES','إجمالي الضرائب')}</td><td>${fmt(calcul.total_taxes)} DZD</td></tr>
      <tr class="grand"><td>${L('COÛT TOTAL RENDU','التكلفة الإجمالية')}</td><td>${fmt(calcul.cout_total)} DZD</td></tr>
    </table>` : '';

  const economiesHtml = economies?.length ? economies.map(e => `<div class="eco">💰 ${e.message}</div>`).join('') : '';

  const html = `<!DOCTYPE html><html dir="${dir}" lang="${isAr ? 'ar' : 'fr'}"><head>
<meta charset="utf-8"><title>TidjarAI Checklist</title>
<style>
  @page { size: A4; margin: 15mm; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Segoe UI', Tahoma, Arial, sans-serif; font-size: 11px; color: #1e293b; line-height: 1.5; direction: ${dir}; }
  .header { background: #0a1e42; color: white; padding: 14px 20px; margin-bottom: 16px; }
  .header h1 { font-size: 18px; letter-spacing: 2px; }
  .header .sub { font-size: 10px; opacity: 0.7; }
  .header .date { float: ${isAr ? 'left' : 'right'}; font-size: 10px; opacity: 0.7; margin-top: -30px; }
  .info { margin-bottom: 12px; font-size: 10px; }
  .info b { color: #0a1e42; }
  h2 { font-size: 12px; color: white; background: #0a1e42; padding: 5px 10px; margin: 12px 0 6px; }
  .doc { padding: 4px 0; border-bottom: 1px solid #e2e8f0; font-size: 10px; }
  .doc .name { font-weight: 600; }
  .doc .det { color: #64748b; font-size: 9px; }
  .doc .timing { color: #3730a3; background: #eef2ff; padding: 1px 4px; border-radius: 3px; }
  .doc .mentions { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 4px; padding: 4px 6px; margin-top: 3px; font-size: 8px; }
  .doc .mentions b { font-size: 8px; color: #0c1425; }
  .doc .mentions ul { margin: 2px 0 0 12px; padding: 0; color: #64748b; }
  .doc .mentions li { margin-bottom: 1px; }
  .doc .howto { color: #6366f1; font-style: italic; font-size: 8px; margin-top: 2px; }
  .doc .warn-line { color: #dc2626; font-weight: 600; font-size: 8px; margin-top: 2px; }
  .fisc { width: 100%; border-collapse: collapse; font-size: 10px; margin-bottom: 10px; }
  .fisc td { padding: 4px 8px; border-bottom: 1px solid #e2e8f0; }
  .fisc td:last-child { text-align: ${isAr ? 'left' : 'right'}; font-weight: 500; }
  .fisc .total td { border-top: 2px solid #0a1e42; font-weight: 700; color: #0a1e42; }
  .fisc .grand td { background: #f1f5f9; font-weight: 700; font-size: 11px; }
  .eco { background: #d1fae5; padding: 5px 8px; border-radius: 4px; margin: 4px 0; font-size: 10px; border-left: 3px solid #059669; }
  .warn { background: #f1f5f9; border: 1px solid #e2e8f0; padding: 8px; font-size: 8px; color: #64748b; margin-top: 14px; border-radius: 4px; }
</style></head><body>
<div class="header">
  <h1>TIDJARAI</h1>
  <div class="sub">${L('CHECKLIST D\'IMPORTATION','قائمة وثائق الاستيراد')}</div>
  <div class="date">${L('Générée le','تاريخ الإنشاء')} : ${date} | ${L('Valable 30 jours','صالحة 30 يوماً')}</div>
</div>
<div class="info">
  <b>${L('Produit','المنتج')} :</b> ${description}<br>
  <b>${L('Pays fournisseur','بلد المورد')} :</b> ${paysNom} (${pays}) &nbsp;|&nbsp;
  <b>${L('Code activité','رمز النشاط')} :</b> ${codeActivite} &nbsp;|&nbsp;
  <b>${L('Code SH','رمز النظام المنسق')} :</b> ${classification.code_sh}<br>
  <b>${L('Description','الوصف')} :</b> ${classification.description}
  ${accord ? `<br><b>${L('Accord','الاتفاقية')} :</b> ${accord.type} — ${accord.document}` : ''}
</div>

<h2>${L('DOCUMENTS FOURNISSEUR (avant expédition)','وثائق المورد (قبل الشحن)')}</h2>
${documents.fournisseur.map(d => `<div class="doc">${d.obligatoire ? '✅' : '⭐'} <span class="name">${d.nom}</span>${d.recommande ? ` <small>(${L('Recommandé','موصى به')})</small>` : ''}${d.timing ? ` <small class="timing">[${d.timing}]</small>` : ''}<div class="det">${d.details}</div>${d.mentions && Array.isArray(d.mentions) ? `<div class="mentions"><b>${L('Mentions obligatoires :','البيانات الإلزامية:')}</b><ul>${d.mentions.map(m => '<li>' + m + '</li>').join('')}</ul></div>` : ''}${d.howto ? `<div class="howto">→ ${d.howto}</div>` : ''}${d.warning ? `<div class="warn-line">⚠️ ${d.warning}</div>` : ''}</div>`).join('')}

<h2>${L('DOCUMENTS IMPORTATEUR (avant arrivée)','وثائق المستورد (قبل الوصول)')}</h2>
${documents.importateur.map(d => `<div class="doc">✅ <span class="name">${d.nom}</span>${d.timing ? ` <small class="timing">[${d.timing}]</small>` : ''}<div class="det">${d.details}</div>${d.mentions && Array.isArray(d.mentions) ? `<div class="mentions"><b>${L('Mentions obligatoires :','البيانات الإلزامية:')}</b><ul>${d.mentions.map(m => '<li>' + m + '</li>').join('')}</ul></div>` : ''}${d.howto ? `<div class="howto">→ ${d.howto}</div>` : ''}${d.warning ? `<div class="warn-line">⚠️ ${d.warning}</div>` : ''}</div>`).join('')}

${calculSection}
${economiesHtml}

<div class="warn">
  <b>⚠️ ${L('AVERTISSEMENT','تحذير')}</b><br>
  ${L(
    'Cette checklist est basée sur la réglementation en vigueur à la date indiquée. Vérifiez toujours les éventuelles nouvelles circulaires. Le classement tarifaire définitif est de la compétence exclusive des Douanes algériennes.',
    'هذه القائمة مبنية على التنظيم الساري المفعول في التاريخ المشار إليه. تحقق دائماً من المناشير الجديدة. التصنيف الجمركي النهائي من اختصاص الجمارك الجزائرية حصرياً.'
  )}
</div>
</body></html>`;

  printHtml(html);
}

export function generatePPIPDF({ entreprise, lignes, date }) {
  const rows = (lignes || []).map((l, i) => `
    <tr>
      <td style="text-align:center">${i + 1}</td>
      <td style="text-align:center">${l.position || ''}</td>
      <td style="text-align:center">${l.sousPosition || ''}</td>
      <td>${l.designation || ''}</td>
      <td style="text-align:center">${l.stock || ''}</td>
      <td style="text-align:center">${l.quantite || ''}</td>
      <td style="text-align:center">${l.unite || ''}</td>
      <td>${l.observations || ''}</td>
    </tr>`).join('');

  // Pad empty rows
  const pad = Math.max(0, 7 - (lignes?.length || 0));
  const emptyRows = Array(pad).fill('<tr>' + '<td>&nbsp;</td>'.repeat(8) + '</tr>').join('');

  const html = `<!DOCTYPE html><html dir="rtl" lang="ar"><head>
<meta charset="utf-8"><title>البرنامج التقديري للاستيراد</title>
<style>
  @page { size: A4 landscape; margin: 10mm; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Segoe UI', Tahoma, Arial, sans-serif; font-size: 12px; direction: rtl; text-align: right; }
  .title { text-align: center; font-size: 16px; font-weight: bold; margin-bottom: 4px; }
  .subtitle { text-align: center; font-size: 14px; font-weight: bold; margin-bottom: 4px; }
  .program { text-align: center; font-size: 14px; font-weight: bold; margin-bottom: 16px; background: #f1f5f9; padding: 6px; }
  .info-table { width: 100%; border-collapse: collapse; margin-bottom: 14px; }
  .info-table td { padding: 4px 8px; font-size: 11px; }
  .info-table .label { font-weight: bold; width: 180px; }
  .info-table .value { border-bottom: 1px solid #ccc; }
  table.main { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
  table.main th { background: #0a1e42; color: white; padding: 6px 4px; font-size: 10px; text-align: center; }
  table.main td { border: 1px solid #ccc; padding: 4px; font-size: 10px; min-height: 22px; }
  .signatures { display: flex; justify-content: space-between; margin-top: 30px; font-size: 11px; }
  .sig-block { text-align: center; width: 40%; }
</style></head><body>
<div class="title">الجمهوريــة الجزائريــة الديمقراطيــة الشعبيــة</div>
<div class="subtitle">وزارة التجارة الخارجية وترقية الصادرات</div>
<div class="program">البرنامج التقديري للسداسي الثاني للاستيراد لسنة ${new Date().getFullYear()}</div>

<table class="info-table">
  <tr><td class="label">رقم السجل التجاري</td><td class="value">${entreprise?.rc || ''}</td><td class="label">الهاتف</td><td class="value">${entreprise?.tel || ''}</td></tr>
  <tr><td class="label">التسمية الاجتماعية للمستورد</td><td class="value">${entreprise?.raisonSociale || ''}</td><td class="label">بريد الالكتروني</td><td class="value">${entreprise?.email || ''}</td></tr>
  <tr><td class="label">رقم التعريف الجبائي</td><td class="value">${entreprise?.nif || ''}</td><td></td><td></td></tr>
</table>

<table class="main">
  <thead><tr>
    <th style="width:35px">الرقم</th>
    <th>البند التعريفي</th>
    <th>البند التعريفي الفرعي</th>
    <th style="width:25%">تعيين المنتوج</th>
    <th>حالة المخزونات الى غاية 30 جوان ${new Date().getFullYear()}</th>
    <th>الكمية المطلوب استيرادها</th>
    <th>الوحدة</th>
    <th>ملاحظات</th>
  </tr></thead>
  <tbody>${rows}${emptyRows}</tbody>
</table>

<div class="signatures">
  <div class="sig-block">امضاء مسير المؤسسة</div>
  <div class="sig-block">تأشيرة مصالح وزارة التجارة الداخلية وضبط السوق الوطنية</div>
</div>
</body></html>`;

  printHtml(html);
}

function fmt(n) { return n?.toLocaleString('fr-DZ') || '0'; }

function printHtml(html) {
  const w = window.open('', '_blank', 'width=900,height=700');
  w.document.write(html);
  w.document.close();
  setTimeout(() => { w.print(); }, 400);
}
